import { db } from './supabase.js'

class SongManager {
  constructor() {
    this.songsCache = new Map()
    this.cacheExpiry = 5 * 60 * 1000 // 5 minutes
  }

  // Load songs from database or fallback to local data
async loadSongs(genre = null, difficulty = null) {
  const cacheKey = `${genre || 'all'}-${difficulty || 'all'}`
  
  // Check cache first
  if (this.songsCache.has(cacheKey)) {
    const cached = this.songsCache.get(cacheKey)
    if (Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data
    }
  }

  try {
    // Try to load from database
    let songs = await db.getSongs(genre, difficulty)
    
    // Special handling for "daily" genre
    if (genre === 'daily') {
      // For daily hits, only get classic songs with high popularity
      songs = await db.getSongs('classic', difficulty)
      
      // Filter for high popularity songs (85+ score) and before 2010
      songs = songs.filter(song => 
        song.popularity_score >= 85 && 
        song.release_year && 
        song.release_year < 1995
      )
      
      // Sort by popularity to ensure we're getting the most popular ones
      songs.sort((a, b) => b.popularity_score - a.popularity_score)
      
      console.log(`Daily hits: ${songs.length} popular classic songs from before 2010 found`)
    }
        
    // NEW: Special handling for "classichiphop" genre
    if (genre === 'classichiphop') {
      // Get hip-hop songs from 2002 or earlier
      songs = await db.getSongs('hiphop', difficulty)
      
      // Filter for songs released in 2002 or earlier
      songs = songs.filter(song => song.release_year && song.release_year <= 2002)
      
      // Sort by release year (newest classic first) and popularity
      songs.sort((a, b) => {
        // First sort by year (descending)
        if (a.release_year !== b.release_year) {
          return b.release_year - a.release_year
        }
        // Then by popularity
        return b.popularity_score - a.popularity_score
      })
      
      console.log(`Classic Hip-Hop: ${songs.length} songs from 2002 or earlier found`)
    }
    
    if (songs && songs.length > 0) {
      // Cache the results
      this.songsCache.set(cacheKey, {
        data: songs,
        timestamp: Date.now()
      })
      return songs
    } else {
      throw new Error('No songs in database')
    }
  } catch (error) {
    console.warn('Failed to load songs from database, using fallback:', error)
    
    // Fallback to local song data
    const fallbackSongs = await this.getFallbackSongs()
    let filteredSongs = this.filterSongs(fallbackSongs, genre, difficulty)
    
    // Apply same logic for fallback songs
    if (genre === 'daily') {
      filteredSongs = this.filterSongs(fallbackSongs, 'classic', difficulty)
        .filter(song => 
          song.popularity_score >= 85 && 
          song.release_year && 
          song.release_year < 1995
        )
        .sort((a, b) => b.popularity_score - a.popularity_score)
    }
    
    // NEW: Apply classic hip-hop logic for fallback songs
    if (genre === 'classichiphop') {
      filteredSongs = this.filterSongs(fallbackSongs, 'hiphop', difficulty)
        .filter(song => song.release_year && song.release_year <= 2002)
        .sort((a, b) => {
          if (a.release_year !== b.release_year) {
            return b.release_year - a.release_year
          }
          return b.popularity_score - a.popularity_score
        })
    }
    
    // Cache the fallback results
    this.songsCache.set(cacheKey, {
      data: filteredSongs,
      timestamp: Date.now()
    })
    
    return filteredSongs
  }
}

  // Get daily song for a specific date, genre, and difficulty
async getDailySong(genre = 'daily', date = new Date(), difficulty = 'easy') {
  // Add this helper function at the beginning of getDailySong
  const createDateSeed = (year, month, day, genre, difficulty) => {
    // Use a prime number multiplier for better distribution
    const dateComponent = ((year - 2020) * 365) + (month * 31) + day;
    const genreHash = genre.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const difficultyHash = difficulty === 'hard' ? 1 : 0;
    
    // Use prime numbers for better distribution
    return (dateComponent * 7919) + (genreHash * 31) + (difficultyHash * 13);
  };

  try {
    const songs = await this.loadSongs(genre, difficulty)
    
    if (!songs || songs.length === 0) {
      console.warn(`No songs found for genre: ${genre}, difficulty: ${difficulty}`)
      return this.getEmergencyFallbackSong()
    }

    // Get both local and UTC dates for comparison
    const localDate = new Date(date)
    const utcDate = new Date(date.toUTCString())
    
    // Use local date for consistency
    const year = localDate.getFullYear()
    const month = localDate.getMonth()
    const day = localDate.getDate()
    
    // Replace the old dateSeed calculation with the new one
    const dateSeed = createDateSeed(year, month, day, genre, difficulty)
    
    // Use the date seed to select a song
    let songIndex = dateSeed % songs.length
    let selectedSong = songs[songIndex]
    
    // Validate and find a valid song if needed
    let attempts = 0
    while (!this.validateSong(selectedSong) && attempts < songs.length) {
      attempts++
      songIndex = (songIndex + 1) % songs.length
      selectedSong = songs[songIndex]
      console.warn(`Song at index ${songIndex - 1} was invalid, trying next song`)
    }
    
    if (!this.validateSong(selectedSong)) {
      console.error('No valid songs found in the entire collection!')
      return this.getEmergencyFallbackSong()
    }
    
    console.log(`Daily song selection:`, {
      localDate: localDate.toISOString(),
      localDateString: `${year}-${month + 1}-${day}`,
      utcDate: utcDate.toISOString(),
      genre,
      difficulty,
      dateSeed,
      totalSongs: songs.length,
      selectedIndex: songIndex,
      selectedSong: `${selectedSong?.title} by ${selectedSong?.artist}`,
      songId: selectedSong?.id
    })
    
    return selectedSong
  } catch (error) {
    console.error('Error getting daily song:', error)
    return this.getEmergencyFallbackSong()
  }
}

  // Filter songs by genre and difficulty
filterSongs(songs, genre = null, difficulty = null) {
  let filtered = [...songs]
  
  // Handle special genres
  if (genre && genre !== 'daily' && genre !== 'classichiphop') {
    filtered = filtered.filter(song => song.genre === genre)
  }
  
  if (difficulty) {
    filtered = filtered.filter(song => song.difficulty === difficulty)
  }
  
  console.log(`Filtered songs: ${filtered.length} found for genre: ${genre}, difficulty: ${difficulty}`)
  
  return filtered
}

  // Fallback songs when database is unavailable
  async getFallbackSongs() {
    // Import local songs data
    try {
      const songsModule = await import('../data/songs.json')
      return songsModule.default || songsModule
    } catch (error) {
      console.warn('Could not load local songs data:', error)
      return this.getHardcodedSongs()
    }
  }

  // Emergency hardcoded songs if everything else fails
  getHardcodedSongs() {
    return [
      {
        id: 1,
        title: "Don't Stop Believin'",
        artist: "Journey",
        genre: "rock",
        difficulty: "easy",
        lyrics: "Just a small town girl, livin' in a _____ world\nShe took the _____ train goin' _____\nJust a city boy, born and _____ in south Detroit\nHe took the _____ train goin' anywhere",
        blanks: ["lonely", "midnight", "anywhere", "raised", "midnight"],
        hints: ["Rhymes with 'only'", "The time when it's darkest", "Any place at all", "Past tense of 'raise'", "The time when it's darkest"]
      },
      {
        id: 2,
        title: "Bohemian Rhapsody",
        artist: "Queen",
        genre: "rock",
        difficulty: "easy",
        lyrics: "Is this the real _____?\nIs this just _____?\nCaught in a _____, no escape from _____\nOpen your _____, look up to the skies and see",
        blanks: ["life", "fantasy", "landslide", "reality", "eyes"],
        hints: ["What we're living", "Not reality, but...", "A geological disaster", "The opposite of fantasy", "What you see with"]
      }
    ]
  }

  // Emergency fallback song
  getEmergencyFallbackSong() {
    return {
      id: 999,
      title: "Don't Stop Believin'",
      artist: "Journey",
      genre: "daily",
      difficulty: "easy",
      lyrics: "Just a small town girl, livin' in a _____ world\nShe took the _____ train goin' _____\nJust a city boy, born and _____ in south Detroit\nHe took the _____ train goin' anywhere",
      blanks: ["lonely", "midnight", "anywhere", "raised", "midnight"],
      hints: ["Rhymes with 'only'", "The time when it's darkest", "Any place at all", "Past tense of 'raise'", "The time when it's darkest"]
    }
  }

  // Validate song data
  validateSong(song) {
    if (!song) return false
    
    const required = ['title', 'artist', 'lyrics', 'blanks', 'hints']
    const hasRequired = required.every(field => song[field] && song[field].length > 0)
    
    if (!hasRequired) return false
    
    // Check if blanks and hints arrays match
    if (song.blanks.length !== song.hints.length) {
      console.warn(`Song ${song.title}: blanks and hints arrays don't match`)
      return false
    }
    
    // Check if number of blanks in lyrics matches blanks array
    const blankCount = (song.lyrics.match(/_____/g) || []).length
    if (blankCount !== song.blanks.length) {
      console.warn(`Song ${song.title}: ${blankCount} blanks in lyrics but ${song.blanks.length} in array`)
      return false
    }
    
    return true
  }

  // Get available genres
async getAvailableGenres() {
  try {
    const songs = await this.loadSongs()
    const genres = [...new Set(songs.map(song => song.genre))]
    
    // Always include daily and classichiphop as options
    const sortedGenres = ['daily', ...genres.filter(g => g !== 'daily').sort(), 'classichiphop']
    
    return sortedGenres
  } catch (error) {
    console.error('Error getting genres:', error)
    return ['daily', 'rock', 'pop', 'hiphop', 'country', 'rb', 'indie', 'classic', 'classichiphop']
  }
}

  // Get song statistics
  async getSongStats() {
    try {
      const songs = await this.loadSongs()
      
      const stats = {
        total: songs.length,
        byGenre: {},
        byDifficulty: {},
        averageBlankCount: 0
      }
      
      let totalBlanks = 0
      
      songs.forEach(song => {
        // Count by genre
        stats.byGenre[song.genre] = (stats.byGenre[song.genre] || 0) + 1
        
        // Count by difficulty
        stats.byDifficulty[song.difficulty] = (stats.byDifficulty[song.difficulty] || 0) + 1
        
        // Count blanks
        totalBlanks += song.blanks.length
      })
      
      stats.averageBlankCount = Math.round(totalBlanks / songs.length)
      
      return stats
    } catch (error) {
      console.error('Error getting song stats:', error)
      return null
    }
  }

  // Clear cache
  clearCache() {
    this.songsCache.clear()
  }

  // Preload songs for better performance
  async preloadGenre(genre, difficulty = null) {
    try {
      await this.loadSongs(genre, difficulty)
      console.log(`Preloaded songs for ${genre}${difficulty ? ` (${difficulty})` : ''}`)
    } catch (error) {
      console.warn(`Failed to preload songs for ${genre}:`, error)
    }
  }
}

// Create singleton instance
export const songManager = new SongManager()

// Export the class for testing
export { SongManager }